type HighlightSegment = {
  text: string;
  highlighted: boolean;
};

/**
 * Parses the input string and returns an array of highlighted segments.
 */
export const parseSearchHighlights = (value: string): HighlightSegment[] => {
  if (!value) {
    return [];
  }

  const segments: HighlightSegment[] = [];
  // Match any of the keywords (tags, id, shortName, description, category) followed by either:
  // - A quoted value: "..." (captures until closing quote)
  // - An unquoted value: any non-space characters
  const regex =
    /(?:tags|id|shortName|description|category):(?:"[^"]*"|[^\s]+)/gi;
  let lastIndex = 0;

  let match = regex.exec(value);
  while (match !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        text: value.substring(lastIndex, match.index),
        highlighted: false
      });
    }

    // Add the highlighted match (tags: plus the value)
    segments.push({
      text: match[0],
      highlighted: true
    });

    lastIndex = match.index + match[0].length;
    match = regex.exec(value);
  }

  // Add remaining text after the last match
  if (lastIndex < value.length) {
    segments.push({
      text: value.substring(lastIndex),
      highlighted: false
    });
  }

  return segments;
};

/**
 * Lowercases without changing length, so an index found in the result still
 * points at the same character of the input. `toLowerCase` maps U+0130 ('İ')
 * to two code units, which shifts every index after it.
 */
const foldCase = (value: string): string => {
  const folded = value.toLowerCase();
  if (folded.length === value.length) {
    return folded;
  }
  return Array.from(value, (character) => {
    const lowered = character.toLowerCase();
    return lowered.length === character.length ? lowered : character;
  }).join('');
};

/**
 * Splits `text` into plain and matching segments, case-insensitively, keeping
 * the casing `text` actually uses.
 *
 * Terms are raw user input — an unescaped `RegExp` throws on `Main(` — so this
 * scans with `indexOf` rather than compiling a pattern.
 */
export const splitOnSearchTerms = (
  text: string,
  terms: string[]
): HighlightSegment[] => {
  if (!text) {
    return [];
  }
  if (terms.length === 0) {
    return [{ text, highlighted: false }];
  }

  const searchTerms = terms
    // an empty term matches at `cursor` with zero length, so the loop below
    // would never advance
    .filter(Boolean)
    .map(foldCase)
    // longest first: the scan keeps the first term matching at an index, so a
    // tie goes to the longer one
    .sort((a, b) => b.length - a.length);
  const foldedText = foldCase(text);
  const segments: HighlightSegment[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    let matchIndex = -1;
    let matchLength = 0;
    for (const term of searchTerms) {
      const index = foldedText.indexOf(term, cursor);
      if (index !== -1 && (matchIndex === -1 || index < matchIndex)) {
        matchIndex = index;
        matchLength = term.length;
      }
    }

    if (matchIndex === -1) {
      segments.push({ text: text.slice(cursor), highlighted: false });
      break;
    }
    if (matchIndex > cursor) {
      segments.push({
        text: text.slice(cursor, matchIndex),
        highlighted: false
      });
    }
    segments.push({
      text: text.slice(matchIndex, matchIndex + matchLength),
      highlighted: true
    });
    cursor = matchIndex + matchLength;
  }

  return segments;
};
